import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Check } from 'lucide-react';
import DashboardLayout from '../layouts/DashboardLayout';
import WalletBalanceCard from '../components/WalletBalanceCard';
import { useAuth } from '../contexts/AuthContext';
import AnnouncementBanner from '../components/home/AnnouncementBanner';
import HeroCard from '../components/home/HeroCard';
import FirstPartyDataChip from '../components/home/FirstPartyDataChip';
import PerformanceColumn from '../components/home/PerformanceColumn';
import AttentionInboxCard from '../components/home/AttentionInboxCard';
import WaveWinsColumn from '../components/home/WaveWinsColumn';
import QuickActionsBar from '../components/home/QuickActionsBar';
import FootprintModal from '../components/home/FootprintModal';
import AdminRefreshDemoCard from '../components/home/AdminRefreshDemoCard';
import { ANNOUNCEMENT } from '../components/home/homeConstants';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const { user } = useAuth();
  const brandLabel = user?.brand_name || 'Brand';
  const [showFootprintModal, setShowFootprintModal] = useState(false);
  const [uploads, setUploads] = useState({ stores: false, customer: false, hashed: false, campaigns: false });
  const [toastMessage, setToastMessage] = useState(null);
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem(`banner-dismissed-${ANNOUNCEMENT.id}`) === '1'; }
    catch { return false; }
  });
  const [heroGlow, setHeroGlow] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [analyticsRes, creatorsRes] = await Promise.all([
          axios.get(`${API}/api/brand/analytics/overview`, { withCredentials: true }),
          axios.get(`${API}/api/brand/creators`, { withCredentials: true }),
        ]);
        const a = analyticsRes.data;
        // Prefer wave-era fields; fall back to legacy campaigns fields.
        const activeCount = a.active_waves || a.active_campaigns || 0;
        const totalCount = a.total_waves || a.total_campaigns || 0;
        const spend = a.wave_spend || a.total_spend || 0;
        const creators = a.wave_creators || creatorsRes.data?.length || 0;
        setLiveMetrics({
          totalSpend: spend,
          totalCampaigns: totalCount,
          activeCampaigns: activeCount,
          totalCreators: creators,
        });
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Analytics fetch failed:', e);
      }
    })();
    const glowTimer = setTimeout(() => setHeroGlow(false), 2200);
    return () => clearTimeout(glowTimer);
  }, []);

  const completedCount = Object.values(uploads).filter(Boolean).length;
  // eslint-disable-next-line no-unused-vars
  const _completed = completedCount; // kept only for FootprintModal progress pips

  const handleUpload = (id) => {
    setUploads(prev => ({ ...prev, [id]: true }));
    setToastMessage('Impact Mapped!');
    setTimeout(() => setToastMessage(null), 2500);
  };

  const dismissBanner = () => {
    try {
      localStorage.setItem(`banner-dismissed-${ANNOUNCEMENT.id}`, '1');
    } catch (e) {
      // Storage disabled (e.g. Safari private mode) — banner will re-appear next load, harmless.
      if (process.env.NODE_ENV !== 'production') console.debug('banner dismiss persist failed:', e?.message);
    }
    setBannerDismissed(true);
  };

  const hero = useMemo(() => {
    const active = liveMetrics?.activeCampaigns ?? 3;
    const projected = liveMetrics ? Math.round((liveMetrics.totalSpend || 0) * 1.35) : 248_000;
    const hasRealData = !!liveMetrics && (liveMetrics.totalSpend > 0 || liveMetrics.activeCampaigns > 0);
    return { active, projected, velocity: '+41%', hasRealData };
  }, [liveMetrics]);

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 space-y-4 max-w-[1500px] mx-auto" data-testid="home-page">

        {!bannerDismissed && <AnnouncementBanner onDismiss={dismissBanner} />}

        {user?.role === 'admin' && <AdminRefreshDemoCard />}

        <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-stretch">
          <HeroCard
            brandLabel={brandLabel}
            hero={hero}
            heroGlow={heroGlow}
          />
          <div className="lg:sticky lg:top-6 self-start">
            <WalletBalanceCard />
          </div>
        </div>

        <FirstPartyDataChip />

        {/* Needs Your Attention — one card aggregating every red/amber signal
            across the platform. The small-business-owner answer to "where do
            I start?" Renders nothing when the brand has no signals. Replaces
            the legacy DraftsDigestCard which was a single-signal callout. */}
        <AttentionInboxCard />

        {/* iter 76: WaveOrchestrationViz removed from home page — the viz
            is now only meaningful when wired to a specific live wave's
            state (see LiveWaveDashboard). On the home page it was decoration,
            not decision support — Brand Pulse in PerformanceColumn already
            covers the at-a-glance question this used to answer. */}

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
          <PerformanceColumn liveMetrics={liveMetrics} />
          <WaveWinsColumn />
        </div>

        <QuickActionsBar />

        {/* iter-90: removed the discreet footer link to /investor — Investor
            Data Room is taken down from public viewing. Footer now shows a
            plain wordmark with no outbound link. */}
        <div className="pt-2 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gray-600">
          <span data-testid="home-footer-wordmark">wavedsp.ai</span>
        </div>

        <FootprintModal
          open={showFootprintModal}
          onOpenChange={setShowFootprintModal}
          uploads={uploads}
          remaining={4 - completedCount}
          allComplete={completedCount >= 4}
          onUpload={handleUpload}
        />

        {toastMessage && (
          <div className="fixed bottom-6 right-6 px-5 py-3 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-bold flex items-center gap-2 animate-slide-in z-50"
               data-testid="upload-toast">
            <Check className="h-4 w-4" />{toastMessage}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
